import React, { useEffect } from "react";

import { useLazyQuery, useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Theme,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import useClientAllergies from "@/hooks/useClientAllergies";
import { useFragment } from "@/hooks/useFragment";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";

import AllergenIngredientListItem from "./AllergenIngredientListItem";
import AllergenFoodStainlessIcon from "../Icons/AllergenFoodStainlessIcon";

interface Props {
  foodId: string;
  fromAdded?: boolean;
  visibility: boolean;
  onAddRecipe: () => void;
  onRemoveRecipe?: () => void;
  onClose: () => void;
}

const HIText = withStyles(Typography, {
  root: {
    fontSize: 16,
    fontWeight: 700,
    color: "#595F68",
  },
});

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    height: "100vh",
    width: "100%",
    display: "grid",
    placeItems: "center",
  },
  paper: {
    width: 500,
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    maxWidth: "90%",
    backgroundColor: colors.white,
    overflowY: "auto",
    padding: theme.spacing(6),
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: 30,
  },
  name: {
    fontSize: 20,
    fontWeight: 700,
  },
  nameHeader: {
    marginTop: 20,
    display: "flex",
    alignItems: "center",
    gap: 15,
  },
  infoHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },
  infoHeaderItems: {
    display: "flex",
    gap: 15,
  },
  ingredients: {
    display: "flex",
    flexDirection: "column",
    maxHeight: 500,
    overflowY: "auto",
    gap: 1,
  },
  ingredientsLabel: {
    marginBottom: 15,
    fontSize: 20,
    fontWeight: 700,
    color: "#595F68",
  },
  addRecipe: {
    backgroundColor: "transparent",
    color: "#FF6262",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  duplicateRecipe: {
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: "#D4DBE5",
    "&:hover": {
      backgroundColor: "rgba(125, 135, 151, 0.15)",
    },
    backgroundColor: "transparent",
    color: colors.primary,
  },
}));

const allergenRecipesModalClientFragment = graphql(`
  fragment AllergenRecipesModal_Client on Client {
    id
    profile {
      id
      firstName
    }
    ...AllergenIngredientListItem_Client
  }
`);

const allergenRecipesModalQuery = graphql(`
  query Recipe($id: ID!, $allergies: [AllergiesKind!]) {
    recipe(id: $id) {
      id
      name
      ingredients {
        ...AllergenIngredientListItem
      }
    }
  }
`);

const allergenRecipesModalFoodQuery = graphql(`
  query AllergenRecipesModal_Food($id: ID!) {
    food(id: $id) {
      id
      name
      sourceId
      carbohydrateGrams
      fatGrams
      proteinGrams
      energyKcal
    }
  }
`);

const duplicateRecipeMutation = graphql(`
  mutation DuplicateRecipe($input: DuplicateRecipeInput!) {
    duplicateRecipe(input: $input) {
      recipe {
        id
      }
    }
  }
`);

const AllergenRecipesModal: React.FC<Props> = ({
  foodId,
  fromAdded,
  visibility,
  onAddRecipe,
  onRemoveRecipe,
  onClose,
}) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const navigate = useNavigate();
  const { clientId, planId } =
    useParams<{
      clientId: string;
      planId: string;
    }>();
  const clientAllergies = useClientAllergies(clientId ?? "");
  const [client] = useFragment(
    allergenRecipesModalClientFragment,
    `Client:${clientId}`
  );
  const [fetchFood, foodQuery] = useLazyQuery(allergenRecipesModalFoodQuery, {
    variables: { id: foodId },
  });

  useEffect(() => {
    if (visibility) fetchFood();
  }, [visibility]);

  const food = foodQuery.data?.food;

  const [fetchRecipe, { data }] = useLazyQuery(allergenRecipesModalQuery, {
    variables: {
      id: food?.sourceId ?? "",
      allergies: clientAllergies,
    },
  });

  const [duplicateRecipe, { loading }] = useMutation(duplicateRecipeMutation);

  const handleDuplicateRecipe = async (): Promise<void> => {
    try {
      const result = await duplicateRecipe({
        variables: { input: { id: food?.sourceId ?? "" } },
      });
      const urlPath = `/database/recipes/${result.data?.duplicateRecipe?.recipe?.id}/edit`;
      navigate(`${urlPath}?allergies=${clientAllergies}`, {
        state: {
          programId: planId,
          clientId: clientId,
          clientFirstName: client?.profile?.firstName,
          step: 2,
        },
      });
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          "Something went wrong while duplicating your recipe. Please try again later."
        )
      );
    }
  };

  useEffect(() => {
    if (visibility) fetchRecipe();
  }, [visibility]);

  return (
    <Modal open={visibility} onClose={onClose} className={classes.root}>
      <div className={classes.modalBody}>
        {food == null || data == null ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding={4}
            minWidth={420}
            minHeight={420}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <div className={classes.modalHeader}>
              <Typography className={classes.modalHeaderText}>
                Allergen Alert
              </Typography>
              <IconButton onClick={onClose} size="large">
                <CloseIcon />
              </IconButton>
            </div>
            <div className={classes.content}>
              <div className={classes.nameHeader}>
                <AllergenFoodStainlessIcon />
                <div className={classes.infoHeader}>
                  <Typography className={classes.name} variant="body1">
                    {food.name}
                  </Typography>
                  <div className={classes.infoHeaderItems}>
                    <HIText variant="body1">kcal : {food.energyKcal}</HIText>
                    <HIText variant="body1">
                      C : {food.carbohydrateGrams}g
                    </HIText>
                    <HIText variant="body1">P : {food.proteinGrams}g</HIText>
                    <HIText variant="body1">F : {food.fatGrams}g</HIText>
                  </div>
                </div>
              </div>
              <div>
                <Typography className={classes.ingredientsLabel}>
                  Ingredients
                </Typography>
                <div className={classes.ingredients}>
                  {data?.recipe?.ingredients?.map((data, index) => (
                    <AllergenIngredientListItem
                      key={index}
                      data={data}
                      client={client}
                      warning={data.food.hasAllergies}
                    />
                  ))}
                </div>
              </div>
              <Box
                display="flex"
                flexDirection="row"
                alignSelf="center"
                gap="20px"
              >
                {fromAdded ? (
                  <Button
                    className={classes.duplicateRecipe}
                    disabled={loading}
                    onClick={handleDuplicateRecipe}
                  >
                    Duplicate Recipe
                  </Button>
                ) : (
                  <Button className={classes.addRecipe} onClick={onAddRecipe}>
                    Add Recipe
                  </Button>
                )}
                {fromAdded ? (
                  <Button disabled={loading} onClick={onRemoveRecipe}>
                    Remove Recipe
                  </Button>
                ) : (
                  <Button disabled={loading} onClick={handleDuplicateRecipe}>
                    Duplicate Recipe
                  </Button>
                )}
              </Box>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AllergenRecipesModal;
